import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Brain, RotateCcw,
  Sparkles, Moon, Sun, RefreshCw, X,
  AlertCircle, List, Clipboard, Copy, CheckCircle,
  History as LucideHistoryIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  processPHDAssistance
} from '../services/geminiService';
import { recordROIEvent, saveModuleSectionHistory, clearModuleSessions, subscribeToModuleSession } from '../services/db';
import { ROIEventType, ReportTemplate } from '../types';
import { auth } from '../firebaseConfig';
import { sanitizeHtml } from '../utils/sanitize';
import DictaphoneMini from './DictaphoneMini';
import AIAssistantMenu from './AIAssistantMenu';

interface PHDAssistanceScreenProps {
  reports: ReportTemplate[];
}

interface PHDResult {
  hallazgos: string;
  conclusion: string;
  diferenciales: string;
  estudio_tipo?: string;
}

const PHDAssistanceScreen: React.FC<PHDAssistanceScreenProps> = ({ reports }) => {
  const [context, setContext] = useState(() => {
    return localStorage.getItem('phd_assistance_context') || '';
  });
  const [selectedReportId, setSelectedReportId] = useState<string | null>(() => {
    return localStorage.getItem('phd_assistance_template_id') || null;
  });
  const [reportSearch, setReportSearch] = useState('');
  const [mod1Favorites, setMod1Favorites] = useState<string[]>(() => {
    const saved = localStorage.getItem("modular1_exclusive_favorites");
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const filteredReports = useMemo(() => {
    let filtered = reports;
    if (showFavoritesOnly) {
      filtered = filtered.filter(r => mod1Favorites.includes(r.id));
    }
    if (reportSearch.trim()) {
      const search = reportSearch.toLowerCase();
      filtered = filtered.filter(r => r.title.toLowerCase().includes(search));
    }

    // Aplicar ordenamiento (Favoritos primero, luego uso)
    return [...filtered].sort((a, b) => {
      const aFav = mod1Favorites.includes(a.id);
      const bFav = mod1Favorites.includes(b.id);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return (b.usageCount || 0) - (a.usageCount || 0);
    });
  }, [reports, reportSearch, showFavoritesOnly, mod1Favorites]);

  const toggleMod1Favorite = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const newFavs = mod1Favorites.includes(id)
      ? mod1Favorites.filter(fid => fid !== id)
      : [...mod1Favorites, id];
    setMod1Favorites(newFavs);
    localStorage.setItem("modular1_exclusive_favorites", JSON.stringify(newFavs));
  };
  const [images, setImages] = useState<{ id: string; data: string; mimeType: string; preview: string }[]>(() => {
    const saved = localStorage.getItem('phd_assistance_images');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<PHDResult | null>(() => {
    const saved = localStorage.getItem('phd_assistance_result');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("module_theme_phd");
    if (saved) return saved === "dark";
    return localStorage.getItem("app_theme") === "dark";
  });

  const errorState = useState<string | null>(null);
  const [error, setError] = errorState;
  const [copyStatus, setCopyStatus] = useState<{ [key: string]: boolean }>({});
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  // Historial de versiones y estados relacionados
  const [sectionHistories, setSectionHistories] = useState<Record<string, string[]>>({
    findings: [],
    conclusion: [],
    differential: []
  });
  const [sectionIndices, setSectionIndices] = useState<Record<string, number>>({
    findings: -1,
    conclusion: -1,
    differential: -1
  });

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || uid === 'guest_dev_offline') return;

    const unsubscribe = subscribeToModuleSession(uid, 'phd_assistance', (sessionData) => {
      if (sessionData && sessionData.histories) {
        setSectionHistories(sessionData.histories);
      }
    });

    return () => unsubscribe();
  }, [auth.currentUser?.uid]);

  useEffect(() => {
    localStorage.setItem('phd_assistance_template_id', selectedReportId || '');
  }, [selectedReportId]);

  // Persistencia de estado
  useEffect(() => {
    localStorage.setItem('phd_assistance_context', context);
  }, [context]);

  useEffect(() => {
    try {
      localStorage.setItem('phd_assistance_images', JSON.stringify(images));
    } catch (e) {
      console.error("Error al guardar imágenes en localStorage (posible límite excedido):", e);
    }
  }, [images]);

  useEffect(() => {
    if (result) {
      localStorage.setItem('phd_assistance_result', JSON.stringify(result));
    } else {
      localStorage.removeItem('phd_assistance_result');
    }
  }, [result]);

  useEffect(() => {
    const handleThemeChange = () => {
      const saved = localStorage.getItem("module_theme_phd");
      if (!saved) {
        setIsDarkMode(localStorage.getItem("app_theme") === "dark");
      }
    };
    window.addEventListener("app_theme_changed", handleThemeChange);
    return () => window.removeEventListener("app_theme_changed", handleThemeChange);
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem("module_theme_phd", next ? "dark" : "day");
    window.dispatchEvent(new Event('module_theme_changed'));
  };

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    const imageItems = Array.from(items).filter(item => item.type.indexOf('image') !== -1);

    if (imageItems.length === 0) return;
    const readImage = (item: DataTransferItem): Promise<{ id: string; data: string; mimeType: string; preview: string } | null> => {
      return new Promise((resolve) => {
        const blob = item.getAsFile();
        if (!blob) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target?.result as string;
          if (!base64) {
            resolve(null);
            return;
          }
          const data = base64.split(',')[1];
          resolve({
            id: Math.random().toString(36).substring(2, 11) + Date.now(),
            data,
            mimeType: blob.type,
            preview: base64
          });
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    };

    Promise.all(imageItems.map(readImage)).then(results => {
      const newImages = results.filter((img): img is NonNullable<typeof img> => img !== null);
      if (newImages.length > 0) {
        setImages(prev => [...prev, ...newImages]);
      }
    }).catch(err => {
      console.error("Error al procesar imágenes pegadas:", err);
      setError("No se han podido procesar algunas de las imágenes adjuntas.");
    });
  }, []);

  const handlePasteButtonClick = async () => {
    try {
      // Intentar leer el portapapeles
      const items = await navigator.clipboard.read();
      let foundImage = false;

      for (const item of items) {
        const imageTypes = item.types.filter(type => type.startsWith('image/'));
        for (const type of imageTypes) {
          foundImage = true;
          const blob = await item.getType(type);
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            if (base64) {
              const data = base64.split(',')[1];
              setImages(prev => [...prev, {
                id: Math.random().toString(36).substring(2, 11) + Date.now(),
                data,
                mimeType: type,
                preview: base64
              }]);
            }
          };
          reader.readAsDataURL(blob);
        }
      }

      if (!foundImage) {
        setError("No se han localizado imágenes en el portapapeles.");
      }
    } catch (err) {
      console.error("Error al pegar desde el portapapeles:", err);
      setError("No se ha podido acceder al portapapeles. Por favor, utilice el comando 'pegar' manual o verifique los permisos.");
    }
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyStatus(prev => ({ ...prev, [key]: true }));
      setCopiedSection(key);
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [key]: false }));
        setCopiedSection(null);
      }, 2000);
    });
  };

  const removeImage = (id: string) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const handleProcess = async () => {
    if (images.length === 0) {
      setError('Por favor, incluya al menos una imagen diagnóstica para el análisis.');
      return;
    }
    if (!context.trim()) {
      setError('Por favor, describa el contexto clínico y el tipo de estudio.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const data = await processPHDAssistance(context, images.map(img => ({ data: img.data, mimeType: img.mimeType })));
      setResult(data);

      // Registrar evento de ROI
      if (auth.currentUser) {
        await recordROIEvent(auth.currentUser.uid, ROIEventType.PHD_ASSISTANCE, 'COMPLEX');
      }
    } catch (err) {
      console.error('Error en Asistencia PHD:', err);
      setError('No se ha podido completar el análisis PhD en este momento. Por favor, reintente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const isHeaderDark = isDarkMode;
  const isBodyDark = isDarkMode;

  const handleResetModule = async () => {
    const userId = auth.currentUser?.uid || 'guest_dev_offline';
    setContext('');
    setImages([]);
    setResult(null);
    setError(null);
    setSectionHistories({ findings: [], conclusion: [], differential: [] });
    setSectionIndices({ findings: -1, conclusion: -1, differential: -1 });

    // Limpiar sesión en Firebase
    await clearModuleSessions(userId, 'phd_assistance');

    localStorage.removeItem('phd_assistance_context');
    localStorage.removeItem('phd_assistance_images');
    localStorage.removeItem('phd_assistance_result');
  };

  return (
    <div
      onPaste={handlePaste}
      className={`flex flex-col h-full transition-colors duration-300 ${isBodyDark ? 'bg-[#1e2024] text-gray-200' : 'bg-[#f8fafc] text-gray-800'}`}
    >
      {/* Header */}
      <div className={`px-6 py-4 border-b shadow-sm z-30 shrink-0 flex justify-between items-center ${isHeaderDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center space-x-3">
          <div className="bg-orange-600 p-2 rounded-xl shadow-md">
            <Brain size={18} className="text-white" />
          </div>
          <div>
            <h1 className={`font-black text-[18px] uppercase tracking-tight font-['Outfit'] ${isHeaderDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Asistencia PHD
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-xl border transition-all ${isHeaderDark ? 'bg-[#1e2024] border-gray-700 text-yellow-400' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}
            title={isDarkMode ? 'Modo Día' : 'Modo Noche'}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={handleResetModule}
            className={`p-2 rounded-xl border transition-all ${isHeaderDark ? 'bg-[#1e2024] border-gray-700 text-gray-400 hover:text-red-400' : 'bg-white border-gray-200 text-gray-400 hover:text-red-500 hover:bg-gray-50'}`}
            title="Limpiar Todo"
          >
            <RotateCcw size={18} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <main className="p-4 md:p-6 max-w-5xl mx-auto flex flex-col gap-6">
          <section className="space-y-6">
            <div className={`p-6 rounded-xl border shadow-sm transition-all ${isBodyDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  IMÁGENES DEL ESTUDIO
                </label>

                <button
                  onClick={handlePasteButtonClick}
                  className={`px-3 py-1.5 rounded-lg border-2 border-dashed flex items-center gap-2 transition-all hover:scale-[1.02] active:scale-95
                    ${isBodyDark ? 'border-gray-700 bg-black/20 text-gray-400 hover:border-gray-500 hover:text-gray-300' : 'bg-orange-50 border-orange-100 text-orange-700 hover:border-orange-300 hover:bg-orange-100'}`}
                >
                  <Clipboard size={14} className={isBodyDark ? 'text-gray-600' : 'text-orange-500'} />
                  <span className="text-[9px] font-black uppercase tracking-widest">Pegar Imágenes</span>
                </button>
              </div>

              {images.length === 0 ? (
                <div className={`h-16 rounded-xl border-2 border-dashed flex items-center justify-center transition-all
                  ${isBodyDark ? 'border-gray-800 bg-black/5' : 'border-gray-50 bg-gray-50/30'}`}>
                  <p className={`text-[9px] font-black uppercase tracking-widest ${isBodyDark ? 'text-gray-700' : 'text-gray-300'}`}>Sin imágenes adjuntas</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  <AnimatePresence>
                    {images.map((img) => (
                      <motion.div
                        key={img.id}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className={`relative aspect-square rounded-xl overflow-hidden border-2 ${isBodyDark ? 'border-gray-700' : 'border-gray-200'} group shadow-md`}
                      >
                        <img src={img.preview} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        <button
                          onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                        >
                          <X size={14} />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>

            <div className={`p-6 rounded-xl border shadow-sm transition-all ${isBodyDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  2. CONTEXTO CLÍNICO
                </label>
                <DictaphoneMini
                  onTranscript={(text) => setContext(prev => prev + (prev ? ' ' : '') + text)}
                  isDark={isBodyDark}
                />
              </div>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Ej: TAC de tórax, paciente varón de 65 años con antecedente de tabaquismo y pérdida de peso de 10kg en el último mes..."
                className={`w-full min-h-[120px] p-4 rounded-xl border outline-none text-[13px] font-medium leading-relaxed transition-all
                  ${isBodyDark
                    ? 'bg-black/20 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-orange-500/50'
                    : 'bg-gray-50/50 border-gray-100 text-slate-700 placeholder-gray-300 focus:border-orange-500/30 focus:bg-white'}`}
              />
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3 text-red-500 text-xs font-bold uppercase tracking-wider">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              onClick={handleProcess}
              disabled={isProcessing}
              className={`w-full py-4 rounded-xl font-black text-[12px] uppercase tracking-[0.2em] shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 text-white
                ${isProcessing
                  ? 'bg-orange-500/50 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-700 shadow-orange-500/20'}`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-3">
                  <RefreshCw className="animate-spin" size={18} />
                  <span>Procesando...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <Sparkles size={18} />
                  <span>Generar Diagnóstico PhD</span>
                </div>
              )}
            </button>

            {/* Output Section */}
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6 pt-6"
              >
                <div className={`p-6 rounded-xl border shadow-sm transition-all ${isBodyDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <List className="text-orange-500" size={16} />
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Hallazgos Radiológicos
                      </label>
                    </div>
                    <button
                      onClick={() => handleCopy(result.hallazgos, 'hallazgos')}
                      className={`p-1.5 rounded-lg border transition-all flex items-center gap-2 ${isBodyDark ? 'bg-gray-700 border-gray-600 text-gray-400 hover:text-white' : 'bg-white border-gray-200 text-gray-400 hover:text-blue-600'}`}
                    >
                      {copyStatus['hallazgos'] ? (
                        <>
                          <CheckCircle size={14} className="text-emerald-500" />
                          <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-500">Copiado</span>
                        </>
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                  <div className={`text-[14px] leading-relaxed font-medium whitespace-pre-wrap ${isBodyDark ? 'text-gray-300' : 'text-slate-700'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(result.hallazgos) }} />
                </div>

                <div className={`p-6 rounded-xl border shadow-sm transition-all ${isBodyDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className="text-orange-500" size={16} />
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Diagnósticos Diferenciales
                      </label>
                    </div>
                    <button
                      onClick={() => handleCopy(result.diferenciales, 'diferenciales')}
                      className={`p-1.5 rounded-lg border transition-all flex items-center gap-2 ${isBodyDark ? 'bg-gray-700 border-gray-600 text-gray-400 hover:text-white' : 'bg-white border-gray-200 text-gray-400 hover:text-blue-600'}`}
                    >
                      {copyStatus['diferenciales'] ? (
                        <>
                          <CheckCircle size={14} className="text-emerald-500" />
                          <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-500">Copiado</span>
                        </>
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                  <div className={`text-[13px] font-medium leading-relaxed whitespace-pre-wrap ${isBodyDark ? 'text-gray-300' : 'text-slate-700'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(result.diferenciales) }} />
                </div>
              </motion.div>
            )}
          </section>

          {/* Version History Navigation (Modular homogeneity) */}
          {Object.values(sectionHistories).some(h => h.length > 0) && (
            <div className="mt-8 space-y-6 border-t border-gray-100 dark:border-gray-800 pt-8 text-center sm:text-left">
              <div className="flex items-center gap-2 mb-2">
                <LucideHistoryIcon size={16} className="text-orange-500" />
                <span className="text-[10px] font-black uppercase tracking-widest">Versiones de Pre-Informe</span>
              </div>

              {['findings', 'conclusion', 'differential'].map(section => {
                const histories = sectionHistories[section] || [];
                const idx = sectionIndices[section];
                if (histories.length === 0) return null;

                return (
                  <div key={section} className={`p-6 rounded-xl border shadow-sm transition-all ${isBodyDark ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{section}</span>
                        <AIAssistantMenu
                          isDark={isBodyDark}
                          versionIndex={idx}
                          historyCount={histories.length}
                          onNavigateHistory={(newIdx) => setSectionIndices(prev => ({ ...prev, [section]: newIdx }))}
                          onPostProcess={() => {}}
                          isProcessing={false}
                          onCopy={() => handleCopy(histories[idx], section)}
                          showCopy={true}
                        />
                      </div>
                    </div>
                    <p className={`text-xs font-medium leading-relaxed mt-2 whitespace-pre-wrap ${isBodyDark ? 'text-gray-300' : 'text-slate-700'}`}>
                      {histories[idx]}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PHDAssistanceScreen;
